/**
 * Resolve the ayah the reader is currently on (playing > prop > first visible in viewport).
 * Ayah element ids are `ayah-{surah}:{ayah}` e.g. `ayah-2:255`.
 */

/**
 * @returns {string|null} ayah id like "2:255"
 */
export function findFirstVisibleAyahId(headerOffsetPx = 90) {
  const els = document.querySelectorAll('[id^="ayah-"]');
  if (!els.length) return null;

  let fallback = null;
  for (let i = 0; i < els.length; i++) {
    const el = els[i];
    const rect = el.getBoundingClientRect();
    const ayahId = el.id.startsWith('ayah-') ? el.id.slice(5) : null;
    if (!ayahId || !ayahId.includes(':')) continue;

    // Fully or partially in the reading viewport below the sticky header
    if (rect.bottom > headerOffsetPx && rect.top < window.innerHeight) {
      return ayahId;
    }
    // Track last ayah that has scrolled above the fold
    if (rect.top < headerOffsetPx) {
      fallback = ayahId;
    }
  }
  return fallback || (els[0]?.id?.startsWith('ayah-') ? els[0].id.slice(5) : null);
}

/**
 * @param {object} opts
 * @param {string|null|undefined} opts.playingAyahKey - from AudioContext currentlyPlayingAyah.ayahKey
 * @param {string|null|undefined} opts.currentAyahId - from SurahTrPage scroll tracker
 * @param {number|string|null|undefined} opts.routeSurahId
 * @returns {{ surahId: number|null, ayahId: string|null, ayahNumber: number|null }}
 */
export function resolveReadingPosition({
  playingAyahKey,
  currentAyahId,
  routeSurahId,
  storedAyahId,
} = {}) {
  const candidates = [
    playingAyahKey,
    currentAyahId,
    storedAyahId,
    findFirstVisibleAyahId(),
  ].filter(Boolean);

  let ayahId = null;
  for (const c of candidates) {
    const s = String(c);
    if (s.includes(':')) {
      ayahId = s;
      break;
    }
  }

  if (ayahId) {
    const [sid, anum] = ayahId.split(':');
    return {
      surahId: Number(sid) || null,
      ayahId,
      ayahNumber: Number(anum) || null,
    };
  }

  const sid = Number(routeSurahId);
  if (Number.isFinite(sid) && sid > 0) {
    return { surahId: sid, ayahId: `${sid}:1`, ayahNumber: 1 };
  }

  return { surahId: null, ayahId: null, ayahNumber: null };
}

/**
 * Active juz for an ayah using parah start markers.
 * @param {Array<{parah_number:number, ayah_id:string}>} parahs
 * @param {string} ayahId
 */
export function resolveActiveJuz(parahs, ayahId) {
  if (!parahs?.length || !ayahId) return null;
  const [sid, anum] = String(ayahId).split(':').map(Number);
  if (!sid || !anum) return null;

  const sorted = [...parahs].sort((a, b) => a.parah_number - b.parah_number);
  let active = sorted[0] || null;
  for (const p of sorted) {
    const [ps, pa] = String(p.ayah_id).split(':').map(Number);
    if (sid > ps || (sid === ps && anum >= pa)) {
      active = p;
    } else {
      break;
    }
  }
  return active;
}
